import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, RefreshCw, Power, PowerOff, Activity, AlertTriangle,
  Clock, CheckCircle2, XCircle, Wifi, WifiOff, Settings, Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { useTheme } from '../contexts/ThemeContext';
import { API } from '../App';

// Définition des services API à gérer (hors composant pour stabilité référentielle)
const API_SERVICES = [
  {
    id: 'ifrap-api',
    name: 'IFRAP-AI',
    description: 'Récupération des éditos via API REST ifrapai.org',
    icon: '🤖',
    endpoints: {
      status: '/ifrap-api/status',
      start: '/ifrap-api/start',
      stop: '/ifrap-api/stop',
      checkNow: '/ifrap-api/check-now',
      },
      color: 'purple',
    },
    {
      id: 'cp-service',
      name: 'Auto-CP',
      description: 'Traitement automatique des communiqués de presse (email)',
      icon: '📧',
      endpoints: {
        status: '/cp/service/status',
        start: '/cp/service/start',
        stop: '/cp/service/stop',
      },
      color: 'blue',
    },
    {
      id: 'tribunes-service',
      name: 'Tribunes',
      description: 'Traitement des tribunes email',
      icon: '✉️',
      endpoints: {
        status: '/tribunes/email/service/status',
        start: '/tribunes/email/service/start',
        stop: '/tribunes/email/service/stop',
      },
      color: 'green',
    },
  ];

const ApiConnectionsManager = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [connections, setConnections] = useState([]);
  const [togglingService, setTogglingService] = useState(null);

  const fetchAllStatuses = useCallback(async () => {
    setRefreshing(true);
    try {
      const results = await Promise.all(
        API_SERVICES.map(async (service) => {
          try {
            const { data } = await axios.get(`${API}${service.endpoints.status}`);
            return {
              ...service,
              status: data,
              error: null,
              online: data.running !== false,
            };
          } catch (err) {
            return {
              ...service,
              status: null,
              error: err.response?.data?.detail || err.message,
              online: false,
            };
          }
        })
      );
      setConnections(results);
    } catch (err) {
      console.error('Erreur récupération statuts:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchAllStatuses();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(fetchAllStatuses, 30000);
    return () => clearInterval(interval);
  }, [fetchAllStatuses]);

  const toggleService = async (service, newState) => {
    setTogglingService(service.id);
    try {
      const endpoint = newState ? service.endpoints.start : service.endpoints.stop;
      await axios.post(`${API}${endpoint}`);
      // Rafraîchir le statut
      await fetchAllStatuses();
    } catch (err) {
      console.error(`Erreur toggle ${service.name}:`, err);
      alert(err.response?.data?.detail || `Erreur: ${err.message}`);
    } finally {
      setTogglingService(null);
    }
  };

  const forceCheck = async (service) => {
    if (!service.endpoints.checkNow) return;
    setTogglingService(service.id);
    try {
      await axios.post(`${API}${service.endpoints.checkNow}`);
      await fetchAllStatuses();
    } catch (err) {
      console.error(`Erreur check ${service.name}:`, err);
      alert(err.response?.data?.detail || `Erreur: ${err.message}`);
    } finally {
      setTogglingService(null);
    }
  };

  const getStatusColor = (conn) => {
    if (conn.error) return 'red';
    if (conn.online) return 'green';
    return 'gray';
  };

  const formatDate = (isoString) => {
    if (!isoString) return '—';
    try {
      return new Date(isoString).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return isoString;
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-slate-950' : 'bg-gray-100'}`}>
        <RefreshCw className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-slate-950 text-slate-100' : 'bg-gray-100 text-gray-900'}`}>
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/profile')}
              className={isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-600 hover:text-gray-900'}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-3">
                <Settings className="w-6 h-6 text-sky-500" />
                Connexions API
              </h1>
              <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                Gestion des services et connexions externes
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAllStatuses}
            disabled={refreshing}
            className={isDark ? 'border-slate-700' : ''}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>

        {/* Services Grid */}
        <div className="space-y-4">
          {connections.map((conn) => {
            const statusColor = getStatusColor(conn);
            const isToggling = togglingService === conn.id;
            
            return (
              <Card 
                key={conn.id} 
                className={`overflow-hidden transition-all ${
                  isDark 
                    ? `bg-slate-900/50 border-slate-800 ${conn.online ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-slate-600'}` 
                    : `bg-white ${conn.online ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-gray-300'}`
                }`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{conn.icon}</span>
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          {conn.name}
                          {conn.online ? (
                            <span className="flex items-center gap-1 text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                              <Wifi className="w-3 h-3" /> En ligne
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs bg-slate-500/20 text-slate-400 px-2 py-0.5 rounded-full">
                              <WifiOff className="w-3 h-3" /> Arrêté
                            </span>
                          )}
                        </CardTitle>
                        <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                          {conn.description}
                        </p>
                      </div>
                    </div>
                    
                    {/* Switch ON/OFF */}
                    <div className="flex items-center gap-3">
                      {conn.endpoints.checkNow && conn.online && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => forceCheck(conn)}
                          disabled={isToggling}
                          className={`text-xs ${isDark ? 'border-slate-700 hover:bg-slate-800' : ''}`}
                        >
                          <Zap className="w-3 h-3 mr-1" />
                          Check Now
                        </Button>
                      )}
                      <div className="flex items-center gap-2">
                        <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                          {conn.online ? 'ON' : 'OFF'}
                        </span>
                        <Switch
                          checked={conn.online}
                          onCheckedChange={(checked) => toggleService(conn, checked)}
                          disabled={isToggling}
                          className={isToggling ? 'opacity-50' : ''}
                        />
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  {conn.error ? (
                    <div className="flex items-center gap-2 text-red-400 text-sm">
                      <AlertTriangle className="w-4 h-4" />
                      {conn.error}
                    </div>
                  ) : conn.status ? (
                    <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                      {/* Stats spécifiques selon le service */}
                      {conn.id === 'ifrap-api' && (
                        <>
                          <div>
                            <div className={`text-xs ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>API Status</div>
                            <div className="font-medium flex items-center gap-1">
                              {conn.status.api_status === 'ok' ? (
                                <><CheckCircle2 className="w-3 h-3 text-green-500" /> OK</>
                              ) : (
                                <><XCircle className="w-3 h-3 text-amber-500" /> {conn.status.api_status}</>
                              )}
                            </div>
                          </div>
                          <div>
                            <div className={`text-xs ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>Polling</div>
                            <div className="font-medium">{(conn.status.polling_interval_seconds || 300) / 60} min</div>
                          </div>
                          <div>
                            <div className={`text-xs ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>Éditos publiés</div>
                            <div className="font-medium">{conn.status.stats?.total_published || 0}</div>
                          </div>
                          <div>
                            <div className={`text-xs ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>Dernier check</div>
                            <div className="font-medium">{formatDate(conn.status.last_check)}</div>
                          </div>
                        </>
                      )}
                      
                      {conn.id === 'cp-service' && (
                        <>
                          <div>
                            <div className={`text-xs ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>Polling</div>
                            <div className="font-medium">{(conn.status.polling_interval || 300) / 60} min</div>
                          </div>
                          <div>
                            <div className={`text-xs ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>CP traités</div>
                            <div className="font-medium">{conn.status.processed_count || 0}</div>
                          </div>
                          <div>
                            <div className={`text-xs ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>Dernier check</div>
                            <div className="font-medium">{formatDate(conn.status.last_check)}</div>
                          </div>
                          <div>
                            <div className={`text-xs ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>Email</div>
                            <div className="font-medium text-xs truncate">{conn.status.email || '—'}</div>
                          </div>
                        </>
                      )}
                      
                      {conn.id === 'tribunes-service' && (
                        <>
                          <div>
                            <div className={`text-xs ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>Boîtes actives</div>
                            <div className="font-medium">{conn.status.active_inboxes || 0}</div>
                          </div>
                          <div>
                            <div className={`text-xs ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>Tribunes traitées</div>
                            <div className="font-medium">{conn.status.total_processed || 0}</div>
                          </div>
                          <div>
                            <div className={`text-xs ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>Dernier check</div>
                            <div className="font-medium">{formatDate(conn.status.last_check)}</div>
                          </div>
                          <div>
                            <div className={`text-xs ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>Erreurs</div>
                            <div className="font-medium">{conn.status.errors_count || 0}</div>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                      Aucune donnée disponible
                    </div>
                  )}
                  
                  {/* Sites mapping pour IFRAP-API */}
                  {conn.id === 'ifrap-api' && conn.status?.sites_mapping && (
                    <div className={`mt-3 pt-3 border-t ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
                      <div className={`text-xs ${isDark ? 'text-slate-600' : 'text-gray-400'} mb-2`}>Mapping des sites</div>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(conn.status.sites_mapping).map(([slug, site]) => (
                          <span key={slug} className={`text-xs px-2 py-1 rounded ${isDark ? 'bg-slate-800 text-slate-400' : 'bg-gray-100 text-gray-600'}`}>
                            {slug} → {site}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info footer */}
        <div className={`mt-8 p-4 rounded-lg ${isDark ? 'bg-slate-900/30 border border-slate-800' : 'bg-gray-50 border border-gray-200'}`}>
          <div className="flex items-start gap-3">
            <Activity className={`w-5 h-5 mt-0.5 ${isDark ? 'text-sky-500' : 'text-sky-600'}`} />
            <div>
              <h3 className="font-medium mb-1">À propos des connexions API</h3>
              <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-600'}`}>
                Les services de polling vérifient périodiquement les sources externes (emails, API) pour récupérer du nouveau contenu.
                Vous pouvez les activer/désactiver individuellement selon vos besoins.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiConnectionsManager;
